import { Config } from './config.js';
const keyMapEntries = {
    ArrowLeft: 'Left',
    ArrowRight: 'Right',
    ArrowUp: 'Up',
    ArrowDown: 'Down',
    Enter: 'Enter',
    l: 'Last',
    ' ': 'Space',
    Backspace: 'Back',
    Escape: 'Escape',
};
const keyHoldMapEntries = {
    Enter: 'EnterHold',
};
const flattenKeyMap = (keyMap, targetMap) => {
    for (const [key, value] of Object.entries(keyMap)) {
        if (value == null)
            continue;
        if (Array.isArray(value)) {
            value.forEach((v) => {
                targetMap[v] = key;
            });
        }
        else {
            targetMap[value] = key;
        }
    }
};
let activeElement;
export const setActiveElement = (elm) => {
    updateFocusPath(elm, activeElement);
    activeElement = elm;
    // Callback for libraries to use signals / refs
    Config.setActiveElement(elm);
};
let focusPath = [];
const updateFocusPath = (currentFocusedElm, prevFocusedElm) => {
    let current = currentFocusedElm;
    const fp = [];
    while (current) {
        if (!current.states.has('focus') || current === currentFocusedElm) {
            current.states.add('focus');
            current.onFocus?.call(current, currentFocusedElm, prevFocusedElm);
            current.onFocusChanged?.call(current, true, currentFocusedElm, prevFocusedElm);
        }
        fp.push(current);
        current = current.parent;
    }
    focusPath.forEach((elm) => {
        if (!fp.includes(elm)) {
            elm.states.remove('focus');
            elm.onBlur?.call(elm, currentFocusedElm, prevFocusedElm);
            elm.onFocusChanged?.call(elm, false, currentFocusedElm, prevFocusedElm);
        }
    });
    focusPath = fp;
    return fp;
};
const propagateKeyDown = (e, mappedEvent, isHold) => {
    let finalFocusElm = undefined;
    for (const elm of focusPath) {
        finalFocusElm = finalFocusElm || elm;
        if (mappedEvent) {
            const onKeyHandler = elm[`on${mappedEvent}`];
            if (onKeyHandler?.call(elm, e, elm, finalFocusElm) === true) {
                break;
            }
        }
        else {
            console.log(`Unhandled key event: ${e.key || e.keyCode}`);
        }
        const fallbackFunction = isHold ? elm.onKeyHold : elm.onKeyPress;
        if (fallbackFunction?.call(elm, e, mappedEvent, elm, finalFocusElm) === true) {
            break;
        }
    }
    return false;
};
const DEFAULT_KEY_HOLD_THRESHOLD = 150; // ms
const keyHoldTimeouts = {};
const keyHoldCallback = (e, mappedKeyHoldEvent) => {
    delete keyHoldTimeouts[e.key || e.keyCode];
    propagateKeyDown(e, mappedKeyHoldEvent, true);
};
const handleKeyEvents = (delay, keypress, keyup) => {
    if (keypress) {
        const key = keypress.key || keypress.keyCode;
        const mappedKeyHoldEvent = keyHoldMapEntries[key];
        const mappedKeyEvent = keyMapEntries[key];
        if (!mappedKeyHoldEvent) {
            propagateKeyDown(keypress, mappedKeyEvent, false);
        }
        else {
            if (keyHoldTimeouts[key]) {
                clearTimeout(keyHoldTimeouts[key]);
            }
            keyHoldTimeouts[key] = window.setTimeout(() => keyHoldCallback(keypress, mappedKeyHoldEvent), delay);
        }
    }
    if (keyup) {
        const key = keyup.key || keyup.keyCode;
        const mappedKeyEvent = keyMapEntries[key];
        if (keyHoldTimeouts[key]) {
            clearTimeout(keyHoldTimeouts[key]);
            delete keyHoldTimeouts[key];
            propagateKeyDown(keyup, mappedKeyEvent, false);
        }
    }
};
export const useFocusManager = ({ userKeyMap, keyHoldOptions, ownerContext = (cb) => {
    cb();
}, } = {}) => {
    if (userKeyMap) {
        flattenKeyMap(userKeyMap, keyMapEntries);
    }
    if (keyHoldOptions?.userKeyHoldMap) {
        flattenKeyMap(keyHoldOptions.userKeyHoldMap, keyHoldMapEntries);
    }
    const delay = keyHoldOptions?.holdThreshold || DEFAULT_KEY_HOLD_THRESHOLD;
    const runKeyEvent = handleKeyEvents.bind(null, delay);
    // Owner context is for frameworks that need effects
    const keyPressHandler = (event) => ownerContext(() => {
        runKeyEvent(event, undefined);
    });
    const keyUpHandler = (event) => ownerContext(() => {
        runKeyEvent(undefined, event);
    });
    document.addEventListener('keyup', keyUpHandler);
    document.addEventListener('keydown', keyPressHandler);
    return {
        cleanup: () => {
            document.removeEventListener('keydown', keyPressHandler);
            document.removeEventListener('keyup', keyUpHandler);
            for (const [_, timeout] of Object.entries(keyHoldTimeouts)) {
                if (timeout)
                    clearTimeout(timeout);
            }
        },
        focusPath: () => focusPath,
    };
};
//# sourceMappingURL=focusManager.js.map